import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Article } from '../types';
import ContentBlock from './ContentBlock';
import { Loader } from 'lucide-react';

interface ArticleListProps {
  type?: 'story' | 'lookbook' | 'news';
  title: string;
}

const ArticleList: React.FC<ArticleListProps> = ({ type, title }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const articlesRef = collection(db, 'articles');
        // Fetch all and filter client side to avoid index requirement for compound queries
        const q = query(articlesRef, orderBy('publishDate', 'desc'));

        const querySnapshot = await getDocs(q);
        const list: Article[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as Omit<Article, 'id'>;
          // Filter hidden (visible defaults to true if undefined)
          // Also filter by type if provided (since we removed server-side where clause)
          if (data.visible !== false && (!type || data.type === type)) {
            list.push({ id: doc.id, ...data });
          }
        });
        setArticles(list);
      } catch (error) {
        console.error("Error fetching articles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [type]);

  if (loading) return (
    <div className="min-h-[50vh] flex items-center justify-center">
        <Loader className="animate-spin" />
    </div>
  );

  return (
    <div className="pt-32 pb-20 px-4 md:px-8 max-w-[1400px] mx-auto min-h-screen">
      <div className="border-b border-black pb-4 mb-12">
        <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter uppercase">{title}</h1>
      </div>

      {articles.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <p>No content found in this category.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-12">
          {articles.map((article) => (
            <div key={article.id} className="md:col-span-6 lg:col-span-4">
              <ContentBlock article={article} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArticleList;
